import React from 'react';
import { formatPhone, formatTime } from '../services/firebase';
import { feeSourcesForRide, acceptToCancelLabel } from '../utils/duesBreakdown';

const amountCell = (value) => (value > 0 ? `₹${value}` : '—');
const ordinal = (n) => (n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`);

// Under a ride that collected a cancellation fee: which passenger, which of his
// cancelled rides, and which Anna was driving to him then.
function FeeSourceRow({ ride, allRides }) {
  const { recorded, items } = feeSourcesForRide(ride, allRides);
  const passenger = `${ride.customerName || 'Passenger'}${ride.customerPhone ? ` (${formatPhone(ride.customerPhone)})` : ''}`;
  return (
    <tr>
      <td colSpan={9} style={{ background: 'rgba(251, 191, 36, 0.06)', borderTop: 0, fontSize: 12, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: 2 }}>
          ₹{ride.cancellationFee} is passenger {passenger}'s fee for an earlier late cancel. {' '}
          <span className="muted" style={{ fontWeight: 400 }}>
            {recorded ? 'Recorded by the server.' : 'Matched from his ride history.'}
          </span>
        </div>
        {items.length === 0 && (
          <div className="muted">The cancelled ride could not be found in the ride history.</div>
        )}
        {items.map((item) => {
          const gap = acceptToCancelLabel(item);
          const anna = item.driverName || item.cancelledRide?.driverName || 'an Anna';
          const where = item.cancelledRide?.pickup?.address;
          return (
            <div key={item.rideId}>
              ₹{item.amount}: he cancelled ride <span className="mono">{item.cancelledRide?.rideNumber || item.rideId}</span>
              {' '}on {formatTime(item.cancelledAt)}{gap ? `, ${gap} after ` : ' after '}
              <strong>{anna}</strong> accepted and was coming to pick him up
              {where ? <span className="muted"> ({where})</span> : null}.
              {item.strike ? <span className="muted"> His {ordinal(item.strike)} late cancel{item.strike === 2 ? '; the 1st was a free warning' : ''}.</span> : null}
            </div>
          );
        })}
      </td>
    </tr>
  );
}

// What one Anna's dues are made of: the split of what he owes now, and every
// ride whose fees were recorded, newest first. `breakdown` is buildDuesBreakdown().
export default function DuesBreakdownModal({ driver, breakdown, allRides = [], onClose }) {
  if (!driver || !breakdown) return null;
  const { pending, owed, charged, rides, notItemised } = breakdown;
  const parts = [
    ['Ride fees', owed.rideFee, '₹5 per ride'],
    ['Night fees', owed.nightFee, 'extra ₹5 per night ride'],
    ['Cancellation fees', owed.cancellationFee, 'a rider\'s earlier cancellation fee he collected in cash'],
    ['Earlier balance', owed.earlier, 'owed from before per-ride fees were recorded (14 Oct), or a manual balance change'],
  ];

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal-sheet"
        style={{ maxWidth: 820, width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 16, padding: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#f8fafc' }}>
              What {driver.name || 'Anna'} owes
            </h3>
            <div className="mono muted" style={{ fontSize: 12, marginTop: 2 }}>
              {driver.vehicleNumber || '—'} · {formatPhone(driver.phone)}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12, padding: '12px 14px', marginBottom: 18,
        }}>
          {parts.filter(([, amount]) => amount > 0).map(([label, amount, hint]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
              <span>{label} <span className="muted" style={{ fontSize: 11 }}>· {hint}</span></span>
              <span className="mono" style={{ fontWeight: 700 }}>₹{amount}</span>
            </div>
          ))}
          <div style={{
            display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 4,
            borderTop: '1px solid rgba(255,255,255,0.08)', fontWeight: 800,
          }}>
            <span>Pending dues</span>
            <span className="mono" style={{ color: pending > 0 ? '#f87171' : '#34d399' }}>₹{pending}</span>
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            Payments are counted against his oldest fees first, so what he owes is his most recent fees.
          </div>
        </div>

        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
          Fees per ride · ₹{charged.total} charged on {rides.length} {rides.length === 1 ? 'ride' : 'rides'}
        </div>
        <div className="table-wrap" style={{ maxHeight: 380, overflowY: 'auto' }}>
          <table className="data">
            <thead>
              <tr>
                <th>COMPLETED</th>
                <th>RIDE</th>
                <th>ROUTE</th>
                <th>FARE</th>
                <th>RIDE FEE</th>
                <th>NIGHT</th>
                <th>CANCELLATION</th>
                <th>TOTAL</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {rides.map(({ ride, fees, unpaid }) => (
                <React.Fragment key={ride.id}>
                <tr>
                  <td className="mono muted" style={{ fontSize: 11 }}>{formatTime(ride.completedAt)}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{ride.rideNumber || ride.id.slice(0, 6)}</td>
                  <td style={{ fontSize: 12, maxWidth: 220 }}>
                    {ride.pickup?.address || '—'} → {ride.destination?.name || ride.destination?.address || '—'}
                  </td>
                  <td className="mono">₹{Number(ride.fare) || 0}</td>
                  <td className="mono">{amountCell(fees.rideFee)}</td>
                  <td className="mono">{amountCell(fees.nightFee)}</td>
                  <td className="mono" style={fees.cancellationFee > 0 ? { color: '#fbbf24', fontWeight: 700 } : undefined}>
                    {amountCell(fees.cancellationFee)}
                  </td>
                  <td className="mono" style={{ fontWeight: 700 }}>₹{fees.total}</td>
                  <td>
                    {unpaid <= 0 ? <span className="badge green">Paid</span>
                      : unpaid < fees.total ? <span className="badge orange">₹{unpaid} unpaid</span>
                        : <span className="badge red">Unpaid</span>}
                  </td>
                </tr>
                {fees.cancellationFee > 0 && <FeeSourceRow ride={ride} allRides={allRides} />}
                </React.Fragment>
              ))}
              {rides.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty">No rides with recorded fees yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {notItemised > 0 && (
          <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
            + {notItemised} older {notItemised === 1 ? 'ride' : 'rides'} from before 14 Oct, when per-ride fees were not recorded.
          </div>
        )}
      </div>
    </div>
  );
}
